import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { NgTemplateOutlet } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { DeliveryNoteRef, OperationRequirement, OperationRequirementService, StagingSummaryEntry } from "../../../_services/operation-requirement.service";
import { ShippingConfig, ShippingConfigContext, ShippingConfigType, versandCellShipped } from "../../../_models/shipping-config.interface";
import { SHIPPING_TYPES } from "../../../_models/shipping-config-rules";
import { ShippingConfigService } from "../../../_services/shipping-config.service";
import { ShippingTargetDialogComponent } from "../shipping-target-dialog/shipping-target-dialog.component";
import { ScheduleChannel, ShippingScheduleDialogComponent } from "./shipping-schedule-dialog/shipping-schedule-dialog.component";
import { ArticleService } from "../../../_services";
import { ArticleTemplate } from "../../../_models";
import { StagingConfigService } from "../../../_services/staging-config.service";

// Versandtypen, die Staging-Artikel mit abdecken (Kunden-Rueckversand NICHT).
const COVERING_TYPES: ShippingConfigType[] = ['technician_shipping', 'store_shipping', 'pickpoint'];

type ActionType = "STAGING" | "VERSAND";

/**
 * Phase 3 (Multi-Config + Mengen-Split): Matrix-Darstellung des Artikelbedarfs
 * auf der Ticketseite. Zeilen = OperationRequirements (gruppiert VERSAND /
 * STAGING), Spalten = aktive ShippingConfigs (+ synthetische Spalte
 * "Staging-Standardversand", solange keine deckende Config existiert).
 *
 * Zellen = Mengen-Split (shipping_config_requirements); Stepper +/- verteilen
 * die Bedarfsmenge ueber die Configs, der unverteilte Rest steht als "offen N"
 * am Zeilenanfang. Bereits versandte/vorbereitete Mengen sind (konservativ auf
 * Zeilenebene) gegen Reduzierung gesperrt.
 */
@Component({
  selector: "app-operation-shipping-matrix",
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, RouterLink, ShippingTargetDialogComponent, ShippingScheduleDialogComponent],
  templateUrl: "./operation-shipping-matrix.component.html",
})
export class OperationShippingMatrixComponent implements OnInit, OnDestroy {
  @Input({ required: true }) operationId!: number;
  // Durchgereicht ans Config-Form (Editor im Spalten-Header).
  @Input() projectId?: number;
  @Input() ticketStoreId?: number;
  @Input() ticketLat?: number;
  @Input() ticketLng?: number;
  @Input() referenceDate?: string;
  @Input() operationTechnicianId?: number;
  // Name des Auftragstechnikers — fuer die Umbuchungs-Anzeige in der
  // Standard-Staging-Spalte (Staging-Standardversand geht an diesen Techniker).
  @Input() operationTechnicianName?: string;

  private reqService = inject(OperationRequirementService);
  private configService = inject(ShippingConfigService);
  private articleService = inject(ArticleService);
  private stagingConfigService = inject(StagingConfigService);

  protected requirements = signal<OperationRequirement[]>([]);
  protected configs = signal<ShippingConfig[]>([]);
  protected isSaving = signal<boolean>(false);
  protected isLoading = signal<boolean>(true);

  // Lokaler Split-State: cells[configId][requirementId] = quantity.
  protected cells = signal<Map<number, Map<number, number>>>(new Map());

  // Lokaler Notes-State: notesByConfig[configId] = Lageristen-Hinweis.
  protected notesByConfig = signal<Map<number, string>>(new Map());
  // Fehlermeldung je Config-Zelle (statt stillem reload, siehe persistNotes).
  protected notesErrors = signal<Map<number, string>>(new Map());

  private saveTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private notesSaveTimers = new Map<number, ReturnType<typeof setTimeout>>();
  // Pro Config höchstens ein laufender Notes-Request (Überhol-Schutz) —
  // kommt während des Requests eine neuere Eingabe, wird nach Abschluss mit
  // dem dann aktuellen Stand nachgesendet.
  private notesInFlight = new Map<number, Promise<void>>();
  private notesDirtyWhileInFlight = new Set<number>();

  // Staging-Versand-Status je Anforderung (operation_requirement_id → Eintrag).
  // Wird genutzt, um in der Standard-Staging-Spalte (ohne deckende Config) die
  // bereits versandte/verzoegerte Menge je Artikel zu zeigen.
  protected stagingSummary = signal<StagingSummaryEntry[]>([]);
  protected stagingSummaryById = computed(() => {
    const map = new Map<number, StagingSummaryEntry>();
    for (const entry of this.stagingSummary()) map.set(entry.operation_requirement_id, entry);
    return map;
  });
  // Lieferscheine des Standard-Stagings (Records ohne Config-Zuordnung) —
  // Anzeige in der Standard-Staging-Spalte der Staging-Gruppen-Header-Zeile.
  protected standardStagingDns = signal<DeliveryNoteRef[]>([]);

  async ngOnInit() {
    await Promise.all([this.reload(), this.loadTemplates()]);
  }

  // Ausstehende Debounce-Saves der Notizen sofort abschicken — wer innerhalb
  // der Debounce-Zeit (600 ms) navigiert oder den Tab wechselt, würde die
  // letzte Eingabe sonst kommentarlos verlieren (hawk#557, Aufgabe C).
  ngOnDestroy() {
    for (const [configId, timer] of this.notesSaveTimers) {
      clearTimeout(timer);
      void this.persistNotes(configId);
    }
    this.notesSaveTimers.clear();
  }

  async reload() {
    this.isLoading.set(true);
    const [reqs, configs, stagingSummary] = await Promise.all([
      this.reqService.loadRequirements(this.operationId),
      this.configService.getMatrixConfigs(this.operationId),
      this.reqService.loadStagingSummary(this.operationId),
    ]);
    this.requirements.set(reqs);
    this.configs.set(configs);
    this.stagingSummary.set(stagingSummary.per_requirement);
    this.standardStagingDns.set(stagingSummary.standard_delivery_notes);
    this.initCellsFrom(configs, reqs);
    this.initNotesFrom(configs);
    this.isLoading.set(false);
  }

  // Initialer Zell-Stand = max(geplanter Split, bereits committed). Aktive
  // Configs sind voll editierbar; archivierte (abgeschlossene) Configs sind nur
  // fuer STAGING-Zuordnungen editierbar (deckender Typ) — ihre VERSAND-Zellen
  // bleiben read-only und stehen nicht im cells-State.
  private initCellsFrom(configs: ShippingConfig[], reqs: OperationRequirement[]) {
    const map = new Map<number, Map<number, number>>();
    for (const c of configs) {
      const splitByReq = new Map<number, number>();
      for (const s of c.requirement_split ?? []) splitByReq.set(s.operation_requirement_id, s.quantity);
      const inner = new Map<number, number>();
      for (const r of reqs) {
        // Staging nur auf deckenden Configs zuweisbar.
        if (!this.canAssign(c, r)) continue;
        // Ein bereits versandter VERSAND-Kanal ist read-only (wird als ✓ gerendert,
        // nicht als Stepper) und gehoert daher nicht in den editierbaren State.
        // Staging-Zellen bleiben (auch bei versandtem Staging) als ueber cellLocked
        // gesperrte Stepper im State — so zaehlen sie weiter in assignedTotal.
        // Gemeinsame Regel mit dem Bulk-Excel-Split-Modell (hawk#558, Aufgabe C).
        if (versandCellShipped(c, r.action_type === 'STAGING')) continue;
        const qty = Math.max(splitByReq.get(r.id) ?? 0, this.cellLocked(c, r));
        if (qty > 0) inner.set(r.id, qty);
      }
      map.set(c.id, inner);
    }
    this.cells.set(map);
  }

  // Committe (versandte/zurueckgehaltene) Mengen dieser Config fuer das
  // Requirement — keyed by operation_requirement_id (VERSAND + STAGING).
  private committedFor(config: ShippingConfig, req: OperationRequirement): { shipped: number; held: number } {
    const e = (config.requirement_committed ?? []).find((x) => x.operation_requirement_id === req.id);
    return { shipped: e?.shipped ?? 0, held: e?.held ?? 0 };
  }

  protected committedShipped(config: ShippingConfig, req: OperationRequirement): number {
    return this.committedFor(config, req).shipped;
  }

  protected committedHeld(config: ShippingConfig, req: OperationRequirement): number {
    return this.committedFor(config, req).held;
  }

  // Gesperrte Mindestmenge dieser Zelle (versandt + zurueckgehalten).
  protected cellLocked(config: ShippingConfig, req: OperationRequirement): number {
    const c = this.committedFor(config, req);
    return c.shipped + c.held;
  }

  // --- Zeilen-Gruppen ---
  protected versandRows = computed(() => this.requirements().filter((r) => r.action_type === 'VERSAND'));
  protected stagingRows = computed(() => this.requirements().filter((r) => r.action_type === 'STAGING'));

  // --- deckende Configs + Standard-Staging-Spalte ---
  // Deckende Configs (aktiv ODER archiviert) koennen Staging-Artikel aufnehmen.
  protected coveringConfigs = computed(() =>
    this.configs().filter((c) => COVERING_TYPES.includes(c.shipping_type)),
  );
  // Die "Standard"-Spalte ist der Umschalt-Gegenpart zu den Config-Spalten:
  // sie zeigt den noch NICHT einer Config zugewiesenen Staging-Rest (poolFor).
  // Sichtbar nur, wenn dort tatsächlich etwas liegt — offen, vorbereitet oder
  // bereits versandt. Ist alles den Configs zugeordnet, verschwindet die Spalte;
  // zieht der Lagerist über einen Config-Stepper Menge ab, wächst der Pool und
  // sie taucht wieder auf.
  protected showStandardStaging = computed(() => this.standardStagingHasAssignment());

  // Erklärt die Standard-Spalte: sie ist kein Versandziel, sondern der Auffang
  // für alles, was keinem zugeordnet ist. Als title-Attribut an der Kachel.
  protected readonly standardStagingHint =
    'Überschüssiger Staging-Bedarf landet immer hier: alles, was keinem expliziten Versandziel zugeordnet ist.\n' +
    'Diese Menge geht als Standardversand an den Auftragstechniker, ' +
    'der Versandzeitpunkt richtet sich nach der KW-Zuordnung.\n' +
    'Wird ein anderer Versand gebraucht, dafür ein eigenes Versandziel anlegen.';

  // Die Standard-Spalte nimmt ausschließlich Staging-Reste auf — normaler
  // Versand braucht immer ein Versandziel. Statt leerer Zellen steht deshalb
  // ein rotes Kreuz über dem ganzen Versandartikel-Block.
  protected readonly shippingArticlesBlockedHint =
    'Versandartikel lassen sich hier nicht zuordnen: die Spalte fängt ausschließlich ' +
    'überschüssige Staging-Artikel auf.\n' +
    'Für normalen Versand bitte ein Versandziel anlegen.';

  // Zeilen, über die sich das Kreuz erstreckt: die Artikelzeilen (bzw. die
  // Leer-Zeile) und – falls sichtbar – die Lageristen-Info. Die Gruppen-Kopfzeile
  // bleibt bewusst außen vor, damit ihr Hintergrund durchläuft.
  protected shippingBlockRowspan = computed(() => {
    const rows = this.versandRows().length || 1;
    return rows + (this.showLageristInfo() ? 1 : 0);
  });

  // Tooltip der Spaltenkachel: Umbuchung mit hinein, damit die Zeile keinen
  // eigenen title braucht. Ein title am Kind würde den der Kachel überdecken —
  // der Hinweis wäre dann ausgerechnet über der Textzeile nicht zu sehen.
  protected standardStagingColumnTooltip(): string {
    const technician = this.operationTechnicianName || 'nicht vergeben';
    return `Umbuchung: ${technician}\n\n${this.standardStagingHint}`;
  }

  // --- Zell-/Zeilen-Helfer ---
  protected cellQty(configId: number, reqId: number): number {
    return this.cells().get(configId)?.get(reqId) ?? 0;
  }

  protected assignedTotal(reqId: number): number {
    let sum = 0;
    for (const inner of this.cells().values()) sum += inner.get(reqId) ?? 0;
    return sum;
  }

  // Bereits ueber Configs mit versandtem VERSAND-Kanal verschickte Menge dieses
  // VERSAND-Requirements — verlaesst den offenen Pool (die Zellen sind read-only
  // und liegen NICHT im editierbaren State, daher hier separat abziehen). Gilt
  // sowohl fuer vollstaendig archivierte als auch fuer teilweise versandte
  // Configs (VERSAND raus, Staging noch offen). Nur VERSAND: Staging-Zuordnungen
  // bleiben als editierbare (gesperrte) Zellen im State → ueber assignedTotal.
  protected versandShippedConsumed(req: OperationRequirement): number {
    if (req.action_type !== 'VERSAND') return 0;
    let sum = 0;
    for (const c of this.configs()) {
      if (!c.versand_shipped) continue;
      const cm = this.committedFor(c, req);
      sum += cm.shipped + cm.held;
    }
    return sum;
  }

  protected poolFor(req: OperationRequirement): number {
    return Math.max(0, req.quantity - this.assignedTotal(req.id) - this.versandShippedConsumed(req));
  }

  // Nur noch INFORMATIV fuer die Zeilen-Anzeige: gesamte ueber Configs mit
  // versandtem VERSAND-Kanal bereits verschickte VERSAND-Menge. Die eigentliche
  // Sperre laeuft per Zelle ueber cellLocked (canDecrement); der versandte Teil
  // ist zusaetzlich ueber versandShippedConsumed/poolFor aus dem Pool reserviert.
  protected lockedFloor(req: OperationRequirement): number {
    return req.action_type === 'VERSAND' ? (req.already_shipped_quantity ?? 0) : 0;
  }

  // Zeile vollstaendig erledigt: VERSAND ueber already_shipped_quantity,
  // STAGING ueber versandt+vorbereitet aus dem Staging-Summary. Markiert die
  // Artikel-Zeile als abgeschlossen (z.B. komplett per PickPoint versandt).
  protected requirementFullyShipped(req: OperationRequirement): boolean {
    if (req.quantity <= 0) return false;
    if (req.action_type === 'VERSAND') return (req.already_shipped_quantity ?? 0) >= req.quantity;
    const s = this.stagingSummaryById().get(req.id);
    return !!s && s.shipped + s.delayed >= req.quantity;
  }

  // Staging-Artikel duerfen nur deckenden Configs (Techniker/Filiale/PickPoint)
  // zugeordnet werden, Versandartikel jeder Config.
  protected canAssign(config: ShippingConfig, req: OperationRequirement): boolean {
    return req.action_type === 'VERSAND' || COVERING_TYPES.includes(config.shipping_type);
  }

  protected canIncrement(config: ShippingConfig, req: OperationRequirement): boolean {
    return this.canAssign(config, req) && this.poolFor(req) > 0;
  }

  protected canDecrement(config: ShippingConfig, req: OperationRequirement): boolean {
    if (this.cellQty(config.id, req.id) <= 0) return false;
    // Per-Config-Lock (VERSAND wie STAGING): die Zelle darf nicht unter die ueber
    // DIESE Config bereits committe (versandt + vorbereitet/verzoegert) Menge
    // fallen. Schon ueber archivierte Configs versandte VERSAND-Mengen liegen
    // separat im Pool (archivedConsumed) und blockieren daher nicht mehr
    // faelschlich die aktive Nachversand-Config.
    return this.cellQty(config.id, req.id) - 1 >= this.cellLocked(config, req);
  }

  protected increment(config: ShippingConfig, req: OperationRequirement) {
    if (!this.canIncrement(config, req)) return;
    this.setCell(config.id, req.id, this.cellQty(config.id, req.id) + 1);
  }

  protected decrement(config: ShippingConfig, req: OperationRequirement) {
    if (!this.canDecrement(config, req)) return;
    this.setCell(config.id, req.id, this.cellQty(config.id, req.id) - 1);
  }

  private setCell(configId: number, reqId: number, qty: number) {
    const map = new Map(this.cells());
    const inner = new Map(map.get(configId) ?? new Map<number, number>());
    if (qty <= 0) inner.delete(reqId);
    else inner.set(reqId, qty);
    map.set(configId, inner);
    this.cells.set(map);
    this.scheduleSave(configId);
  }

  private scheduleSave(configId: number) {
    const existing = this.saveTimers.get(configId);
    if (existing) clearTimeout(existing);
    this.saveTimers.set(configId, setTimeout(() => void this.persist(configId), 500));
  }

  private async persist(configId: number) {
    const inner = this.cells().get(configId) ?? new Map<number, number>();
    const requirements = Array.from(inner.entries())
      .filter(([, q]) => q > 0)
      .map(([operation_requirement_id, quantity]) => ({ operation_requirement_id, quantity }));
    this.isSaving.set(true);
    try {
      await this.configService.saveConfigSplit(this.operationId, configId, requirements);
    } catch {
      // Bei Konflikt (z.B. Ueberbuchung durch parallele Aenderung) den Server-
      // Stand uebernehmen.
      await this.reload();
    } finally {
      this.isSaving.set(false);
    }
  }

  // --- Lageristen-Notiz (notes) je Config ---
  // Initialer Notes-Stand aus den geladenen Configs.
  private initNotesFrom(configs: ShippingConfig[]) {
    const map = new Map<number, string>();
    for (const c of configs) map.set(c.id, c.notes ?? '');
    this.notesByConfig.set(map);
  }

  protected configNotes(configId: number): string {
    return this.notesByConfig().get(configId) ?? '';
  }

  // Info-Zeile für den Lageristen nur zeigen, wenn mindestens einer Config ein
  // NORMALER Versandartikel zugeordnet ist (Staging-Zuordnungen zählen nicht) —
  // die Notiz betrifft ausschließlich den normalen Versand.
  protected showLageristInfo = computed(() =>
    this.configs().some((c) => this.configHasGroupAssignment(c, 'VERSAND')),
  );

  // Notiz ist nur editierbar, solange die Config NICHT abgeschlossen
  // (archiviert) ist — bei abgeschlossenem Versand (normaler Versand fertig,
  // Staging gegenstandslos) wird der Hinweis nur noch read-only angezeigt.
  // Aktive Configs (auch teilweise versandte) bleiben editierbar.
  protected canEditNotes(config: ShippingConfig): boolean {
    // Die Lageristen-Info gehört zum normalen Versand-Paket: Sobald der
    // VERSAND-Kanal abgeschlossen ist, ist sie eingefroren. Ein reiner
    // Staging-Abschluss sperrt nicht — das VERSAND-Paket kommt ja noch.
    return !config.archived && !config.versand_shipped;
  }

  protected onNotesInput(configId: number, event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    const map = new Map(this.notesByConfig());
    map.set(configId, value);
    this.notesByConfig.set(map);
    this.scheduleNotesSave(configId);
  }

  private scheduleNotesSave(configId: number) {
    const existing = this.notesSaveTimers.get(configId);
    if (existing) clearTimeout(existing);
    this.notesSaveTimers.set(configId, setTimeout(() => {
      // Timer-Eintrag vor dem Save räumen, damit der ngOnDestroy-Flush ihn
      // nicht ein zweites Mal abschickt.
      this.notesSaveTimers.delete(configId);
      void this.persistNotes(configId);
    }, 600));
  }

  // Robustheit (hawk#557, Aufgabe C):
  // - Pro Config läuft höchstens ein Request; neuere Eingaben während eines
  //   laufenden Requests werden danach mit dem aktuellen Stand nachgesendet —
  //   ein hängender älterer POST kann einen neueren nicht mehr überholen.
  // - Editierbarkeit wird unmittelbar vor dem POST erneut geprüft (Race mit
  //   zeitgleichem VERSAND-Abschluss).
  // - Fehler machen KEIN reload() mehr (das verwarf ungespeicherte Eingaben
  //   aller Spalten kommentarlos): Meldung an der Zelle anzeigen und nur die
  //   betroffene Notiz auf den Server-Stand zurücksetzen.
  private async persistNotes(configId: number) {
    if (this.notesInFlight.has(configId)) {
      this.notesDirtyWhileInFlight.add(configId);
      return;
    }

    const config = this.configs().find((c) => c.id === configId);
    if (!config) return;
    if (!this.canEditNotes(config)) {
      this.setNotesError(configId, 'Versand abgeschlossen — Info für Lageristen nicht mehr änderbar.');
      this.resetNoteToServerState(configId, config);
      return;
    }

    const notes = this.notesByConfig().get(configId) ?? '';
    this.isSaving.set(true);
    const request = (async () => {
      try {
        await this.configService.updateConfigNotes(this.operationId, configId, notes);
        // Erfolgreicher Save = neuer Server-Stand für spätere Zell-Resets.
        config.notes = notes;
        this.setNotesError(configId, null);
      } catch (err) {
        const serverError = err instanceof HttpErrorResponse ? err.error?.error : null;
        this.setNotesError(configId, serverError || 'Speichern fehlgeschlagen — Eingabe wurde auf den letzten gespeicherten Stand zurückgesetzt.');
        this.resetNoteToServerState(configId, config);
      }
    })();
    this.notesInFlight.set(configId, request);
    try {
      await request;
    } finally {
      this.notesInFlight.delete(configId);
      this.isSaving.set(false);
    }

    // Während des Requests kam eine neuere Eingabe → aktuellen Stand senden.
    if (this.notesDirtyWhileInFlight.delete(configId)) void this.persistNotes(configId);
  }

  private setNotesError(configId: number, message: string | null) {
    const map = new Map(this.notesErrors());
    if (message) map.set(configId, message);
    else map.delete(configId);
    this.notesErrors.set(map);
  }

  // Nur die betroffene Zelle auf den letzten bekannten Server-Stand setzen —
  // bewusst kein Voll-reload(), der fremde ungespeicherte Eingaben verwirft.
  private resetNoteToServerState(configId: number, config: ShippingConfig) {
    const map = new Map(this.notesByConfig());
    map.set(configId, config.notes ?? '');
    this.notesByConfig.set(map);
  }

  // --- Config-Spalten verwalten ---
  // "+ Versandziel": NICHT sofort anlegen, sondern den geführten Dialog öffnen.
  // Die Config entsteht erst beim Speichern im Dialog — inklusive der still
  // gesetzten Termin-Defaults der Auftragsgruppe.
  protected async addConfig() {
    await this.openTargetDialog(null);
  }

  // Config mit durchgefuehrter/vorbereiteter Sendung (Lieferscheine oder
  // committe Mengen) darf nicht geloescht werden — der Backend-destroy lehnt
  // das ebenfalls ab (422).
  protected configHasCommitted(c: ShippingConfig): boolean {
    if ((c.delivery_notes?.length ?? 0) > 0) return true;
    return (c.requirement_committed ?? []).some((x) => x.shipped + x.held > 0);
  }

  protected canDeleteConfig(c: ShippingConfig): boolean {
    return !c.archived && !this.configHasCommitted(c);
  }

  // --- Lieferschein-Popup (Versand-, Staging- und Standard-Staging-DNs) ---
  protected dnPopup = signal<{ title: string; badge: string; notes: DeliveryNoteRef[] } | null>(null);

  protected openVersandDnPopup(config: ShippingConfig) {
    this.dnPopup.set({ title: 'Lieferscheine (Versand)', badge: this.configLabel(config), notes: config.delivery_notes ?? [] });
  }

  protected openStagingDnPopup(config: ShippingConfig) {
    this.dnPopup.set({ title: 'Lieferscheine (Staging)', badge: this.configLabel(config), notes: config.staging_delivery_notes ?? [] });
  }

  protected openStandardStagingDnPopup() {
    this.dnPopup.set({ title: 'Lieferscheine (Staging)', badge: 'Staging-Standardversand', notes: this.standardStagingDns() });
  }

  protected closeDnPopup() {
    this.dnPopup.set(null);
  }

  // --- Config-Editor (Popup) ---
  protected editingConfig = signal<ShippingConfig | null>(null);
  // true = Editor im "Neu anlegen"-Modus (Config wird erst beim Speichern erzeugt).
  protected creatingConfig = signal<boolean>(false);
  protected editorContext = signal<ShippingConfigContext | null>(null);
  // Kontext wird geladen — bis dahin bleibt der Dialog zu.
  protected editorLoading = signal<boolean>(false);

  protected async openEditor(config: ShippingConfig) {
    await this.openTargetDialog(config);
  }

  // Der Dialog erwartet einen fertig geladenen Kontext (Auftragsgruppe,
  // Termin-Defaults, Filiale) — deshalb erst laden, dann öffnen. Sonst
  // berechnete er die stillen Termin-Defaults auf leerem Kontext.
  private async openTargetDialog(config: ShippingConfig | null) {
    this.editorLoading.set(true);
    try {
      this.editorContext.set(await this.configService.getContext(this.operationId));
    } catch {
      this.editorContext.set({ customer_has_return_address: false });
    } finally {
      this.editorLoading.set(false);
    }
    this.editingConfig.set(config);
    this.creatingConfig.set(config === null);
  }

  protected closeEditor() {
    this.editingConfig.set(null);
    this.creatingConfig.set(false);
  }

  // --- Versandzeitpunkt-Dialog (Datumszelle je Kanal) ---
  protected scheduleDialog = signal<{ config: ShippingConfig; channel: ScheduleChannel } | null>(null);

  // Lädt den Kontext einmalig und hält ihn — der Termin-Dialog braucht daraus
  // nur die Versandauftrags-Flag, ein Request pro Öffnen wäre Verschwendung.
  private async ensureEditorContext() {
    if (this.editorContext()) return;
    try {
      this.editorContext.set(await this.configService.getContext(this.operationId));
    } catch {
      this.editorContext.set({ customer_has_return_address: false });
    }
  }

  protected async openScheduleDialog(config: ShippingConfig, channel: ScheduleChannel) {
    // Versandter Kanal ist eingefroren — die Zelle ist dann gar nicht klickbar,
    // dieser Guard ist die zweite Linie.
    const shipped = channel === "STANDARD" ? config.versand_shipped : config.staging_shipped;
    if (shipped) return;
    await this.ensureEditorContext();
    this.scheduleDialog.set({ config, channel });
  }

  protected closeScheduleDialog() {
    this.scheduleDialog.set(null);
  }

  // Bewusst KEIN reload(): der Termin-Endpoint liefert den warehouse_app_hash,
  // dem die Matrix-Anreicherungen (archived, requirement_committed, staging_kw,
  // staging_delivery_notes) fehlen — die bleiben durch den Merge erhalten. Ein
  // Voll-Reload würde außerdem Stepper-Eingaben im Debounce-Fenster verwerfen.
  protected onScheduleSaved(updated: ShippingConfig) {
    this.configs.update((list) => list.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
    this.closeScheduleDialog();
  }

  // Der Dialog persistiert selbst; die Matrix lädt nur den neuen Stand.
  protected async onTargetSaved() {
    this.closeEditor();
    await this.reload();
  }

  // --- Anzeige-Helfer ---
  protected configLabel(c: ShippingConfig): string {
    return c.shipping_type_label || c.shipping_type;
  }

  // Überschrift der Spaltenkachel: das Backend-Label enthält bei Filialversand
  // mit Verbrauch den Zusatz "(verbraucht)" (siehe ShippingConfig#shipping_type_label).
  // In der Kachel steht darunter ohnehin "Umbuchung: Verbraucht" — deshalb hier
  // raus, statt den Hash anzufassen.
  protected configHeaderLabel(c: ShippingConfig): string {
    const label = this.configLabel(c);
    return c.shipping_type === "store_shipping" && c.article_disposition === "consumed"
      ? label.replace(" (verbraucht)", "")
      : label;
  }

  // Reiner Typ-Name ohne Ziel — für Kacheln, die das Ziel in einer eigenen
  // Zeile zeigen (Versandziel-Auswahl im Artikel-Dialog). Der Verbrauch-Zusatz
  // bleibt, weil dort sonst nicht sichtbar wäre, dass nicht umgebucht wird.
  protected configTypeLabel(c: ShippingConfig): string {
    const base = SHIPPING_TYPES.find((t) => t.value === c.shipping_type)?.label ?? c.shipping_type;
    return c.shipping_type === "store_shipping" && c.article_disposition === "consumed"
      ? `${base} (verbraucht)`
      : base;
  }

  // Wohin die Sendung geht — je Versandtyp die aussagekräftige Zieladresse.
  // Ergänzt materialRebookingLabel, das zeigt, auf wen umgebucht wird.
  protected configTargetLabel(c: ShippingConfig): string {
    switch (c.shipping_type) {
      case "pickpoint":
        return c.pickpoint_station
          ? `PP-${c.pickpoint_station.pp_id} ${c.pickpoint_station.name}`
          : "Station offen";
      case "store_shipping":
        return c.target_store
          ? [c.target_store.nr, c.target_store.name].filter(Boolean).join(" ")
          : "Ticketfiliale";
      case "technician_shipping":
        return c.resolved_target_user_name || c.target_user_name || "Techniker offen";
      case "customer_shipping":
        return c.target_customer_name || "Projekt-Kunde";
      default:
        return "";
    }
  }

  // Termin des Kanals, um den es im Artikel-Dialog gerade geht — Versandartikel
  // sehen den Standardversand-Termin, Staging-Artikel den Staging-Termin.
  protected configChannelDate(c: ShippingConfig, actionType: ActionType): string {
    const label = actionType === "STAGING" ? this.configStagingLabel(c) : this.configVersandDate(c);
    return label || "kein Termin";
  }

  protected reqLabel(r: OperationRequirement): string {
    return r.article_template_description || r.free_text || '—';
  }

  // Zweiter Bedarf-Zusatz: Artikelvorlagen-ID bei echten Artikeln,
  // sonst Hinweis auf Mitgeb-/Freitext-Artikel.
  protected reqArticleInfo(r: OperationRequirement): string {
    return r.article_template_id ? `ID-${r.article_template_id}` : 'Mitgeb-Artikel';
  }

  // Zweite Header-Zeile: wie das Material dieser Config umgebucht wird.
  // Verbraucht (consumed) bzw. Kunden-Rueckversand → "Verbraucht", sonst das
  // Ziel-(Umbuchungs-)Lager (Technikerversand, PickPoint, Filialversand „auf
  // Techniker umbuchen").
  protected materialRebookingLabel(c: ShippingConfig): string {
    if (c.article_disposition === 'consumed' || c.shipping_type === 'customer_shipping') {
      return 'Umbuchung: Verbraucht';
    }
    // Regel "Techniker vom Auftrag": den aufgelösten Techniker anzeigen.
    // Ist keiner auflösbar, "nicht vergeben".
    if (c.target_user_from_operation) {
      const resolved = c.resolved_target_user_name || 'nicht vergeben';
      return `Umbuchung: ${resolved}`;
    }
    const target = c.target_warehouse?.label || c.resolved_target_user_name || c.target_user_name || 'nicht vergeben';
    return `Umbuchung: ${target}`;
  }

  // Summe der ueber Configs versandten / zurueckgehaltenen Staging-Menge dieses
  // Requirements (ueber alle Spalten) — Basis, um den Standard-Anteil sauber vom
  // Config-Anteil zu trennen.
  private sumCommittedShipped(req: OperationRequirement): number {
    return this.configs().reduce((s, c) => s + this.committedShipped(c, req), 0);
  }

  private sumCommittedHeld(req: OperationRequirement): number {
    return this.configs().reduce((s, c) => s + this.committedHeld(c, req), 0);
  }

  // In der Standard-Staging-Spalte (ohne deckende Config) versandte/verzoegerte
  // Mengen — NUR der Anteil, der NICHT einer Config zugeordnet ist. Das
  // Staging-Summary zaehlt alle Records; die config-zugeordneten Mengen werden
  // bereits in der jeweiligen Config-Spalte angezeigt, daher hier abziehen
  // (sonst erscheint z.B. ein verzoegerter PickPoint-Record doppelt).
  protected standardStagingShipped(req: OperationRequirement): number {
    const total = this.stagingSummaryById().get(req.id)?.shipped ?? 0;
    return Math.max(0, total - this.sumCommittedShipped(req));
  }

  protected standardStagingDelayed(req: OperationRequirement): number {
    const total = this.stagingSummaryById().get(req.id)?.delayed ?? 0;
    return Math.max(0, total - this.sumCommittedHeld(req));
  }

  // --- Datum-/Timing-Infos fuer die Section-Header + verzoegertes Staging ----
  private formatShortDate(iso?: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Geplanter VERSAND-Termin dieser Config — effektiver Termin (festes
  // shipping_date ODER Offset-Regel "X Werktage vor Auftragsdatum"). null = am
  // Auftragsdatum (kein expliziter Termin). Versandfenster „Zeitraum vor
  // Auftrag": chronologische Range „früh – spät" (wie in der LagerApp),
  // kompakt ohne redundante Monats-/Jahresangabe (z.B. „23.–28.07.2026",
  // „29.07.–01.08.2026"); der späteste Tag bleibt der maßgebliche Termin.
  protected configVersandDate(config: ShippingConfig): string | null {
    const latestIso = config.effective_shipping_date ?? config.shipping_date;
    const earliestIso = config.effective_earliest_shipping_date ?? config.earliest_shipping_date;
    if (earliestIso && latestIso) {
      const range = this.formatCompactDateRange(earliestIso, latestIso);
      if (range) return range;
    }
    return this.formatShortDate(latestIso);
  }

  // Kompakte Datums-Range: gleicher Monat+Jahr → „23.–28.07.2026", gleiches
  // Jahr → „29.07.–01.08.2026", sonst beide Daten voll ausgeschrieben.
  private formatCompactDateRange(fromIso: string, toIso: string): string | null {
    const from = new Date(fromIso);
    const to = new Date(toIso);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return null;

    const pad = (n: number) => String(n).padStart(2, '0');
    const full = (d: Date) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    if (from.getFullYear() !== to.getFullYear()) return `${full(from)} – ${full(to)}`;
    if (from.getMonth() !== to.getMonth()) return `${pad(from.getDate())}.${pad(from.getMonth() + 1)}.–${full(to)}`;
    return `${pad(from.getDate())}.–${full(to)}`;
  }

  // Verzoegerter Staging-Versand dieser Config — effektiver Termin (fester
  // staging_ship_ready_at ODER Offset-Regel). null = keine Verzoegerung.
  protected configStagingDelayDate(config: ShippingConfig): string | null {
    return this.formatShortDate(config.effective_staging_ship_ready_at ?? config.staging_ship_ready_at);
  }

  // Anzeige der Staging-Zelle: expliziter Termin, sonst die geplante KW.
  // Leer = noch gar kein Termin gesetzt.
  protected configStagingLabel(config: ShippingConfig): string {
    const date = this.configStagingDelayDate(config);
    if (date) return date;
    const kw = this.configStagingKw(config);
    return kw ? `KW ${kw}` : "";
  }

  // Geplante Staging-KW dieser Config (aus der Wochenzuordnung der Staging-
  // config) — Fallback fuer den Staging-Gruppen-Header, wenn kein explizites
  // Staging-Datum (staging_ship_ready_at) gesetzt ist.
  protected configStagingKw(config: ShippingConfig): number | null {
    return config.staging_kw ?? null;
  }

  // Auftragsweite Staging-KW — alle Configs tragen denselben Wert (der Backend-
  // Wert kommt aus der Wochenzuordnung der Operation). Wird fuer die
  // Standard-Staging-Spalte (ohne Config) genutzt.
  protected operationStagingKw = computed<number | null>(() => {
    const c = this.configs().find((x) => x.staging_kw != null);
    return c?.staging_kw ?? null;
  });

  // Liegt in der Standard-Staging-Spalte (Technikerversand ohne Config)
  // ueberhaupt eine Menge (offen, versandt oder vorbereitet)? Steuert, ob im
  // Staging-Header der Standard-Spalte die KW angezeigt wird.
  private standardStagingHasAssignment(): boolean {
    return this.stagingRows().some(
      (req) => this.poolFor(req) > 0 || this.standardStagingShipped(req) > 0 || this.standardStagingDelayed(req) > 0,
    );
  }

  // Hat die aktuell im Editor geoeffnete Config mind. einen Staging-Artikel
  // zugeordnet? Dann ist Kunden-Rueckversand im Formular gesperrt. Beim Neu-
  // Anlegen (editingConfig == null) gibt es noch keine Zuordnung → false.
  // Darf die geöffnete Config entfernt werden? Steuert den roten Button in der
  // Dialog-Fußzeile; das Backend lehnt das Löschen mit Sendungen ebenfalls ab.
  protected editingConfigCanDelete = computed<boolean>(() => {
    const c = this.editingConfig();
    return c != null && this.canDeleteConfig(c);
  });

  // Hat die geöffnete Config bereits vorbereitete/versandte Mengen? Dann ist
  // im Dialog kein Typwechsel mehr erlaubt.
  protected editingConfigHasCommitted = computed<boolean>(() => {
    const c = this.editingConfig();
    return c != null && this.configHasCommitted(c);
  });

  protected editingConfigHasStaging = computed<boolean>(() => {
    const c = this.editingConfig();
    return c != null && this.configHasGroupAssignment(c, 'STAGING');
  });

  // Ist dieser Config mindestens ein Artikel der Gruppe (VERSAND/STAGING)
  // zugeordnet? Beruecksichtigt sowohl die editierbare Split-Menge (cells) als
  // auch bereits committe Mengen (versandt/zurueckgehalten, die nicht mehr im
  // editierbaren State liegen). Steuert, ob im Gruppen-Header dieser Spalte ein
  // Versand-/Staging-Datum angezeigt wird — sonst (kein zugeordneter Artikel)
  // bleibt die Header-Zelle leer, um Verwirrung zu vermeiden.
  protected configHasGroupAssignment(config: ShippingConfig, actionType: ActionType): boolean {
    const rows = actionType === 'VERSAND' ? this.versandRows() : this.stagingRows();
    return rows.some((req) => this.cellQty(config.id, req.id) > 0 || this.cellLocked(config, req) > 0);
  }

  // ===========================================================================
  // Artikel-CRUD (Anlegen/Bearbeiten/Loeschen + Legacy-Parsing). Frueher in der
  // separaten operation-requirements-Komponente; seit Phase 3 produktiv hier.
  // ===========================================================================

  protected showModal = signal<boolean>(false);
  protected editingRequirement = signal<OperationRequirement | null>(null);
  protected searchResults = signal<ArticleTemplate[]>([]);
  protected selectedTemplate = signal<ArticleTemplate | null>(null);
  protected parsingInProgress = signal<boolean>(false);

  // Artikelvorlagen: alle (Versand-Suche) + nur Staging-Vorlagen des Projekts.
  protected allTemplates = signal<ArticleTemplate[]>([]);
  protected stagingTemplates = signal<{ id: number; description: string }[]>([]);

  // Formular-Felder. formActionType wird durch den jeweiligen Section-Add-Button
  // gesetzt — kein Type-Switch im Modal.
  protected formActionType: ActionType = "VERSAND";
  protected formQuantity = 1;
  protected formSearchText = "";
  protected formStagingTemplateId: number | null = null;

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  private async loadTemplates() {
    // Alle Artikelvorlagen laden (fuer Versand-Suche)
    const all = await firstValueFrom(this.articleService.loadArticleTemplates());
    this.allTemplates.set(all || []);

    // Staging-Artikelvorlagen aus den Staging-Configs des Projekts laden
    if (this.projectId) {
      await this.stagingConfigService.loadConfigs(this.projectId);
      const configs = this.stagingConfigService.configs();
      const templates = configs
        .filter((c) => c.enabled && c.article_template_id && c.article_template_description)
        .map((c) => ({ id: c.article_template_id!, description: c.article_template_description! }));
      const unique = [...new Map(templates.map((t) => [t.id, t])).values()];
      this.stagingTemplates.set(unique);
    }
  }

  // --- Versandziel-Auswahl im Artikel-Dialog ---
  // Wert: Config-ID, "none" (bewusst kein Ziel → Staging-Standardversand),
  // "new" (öffnet den Versandziel-Dialog) oder null (Backend wählt selbst).
  protected formConfigId = signal<number | "none" | "new" | null>(null);
  protected formError = signal<string | null>(null);
  // Auswahl vor dem Sprung in "Neues Versandziel…" — für den Abbruch-Fall.
  private previousFormConfigId: number | "none" | null = null;
  protected creatingTargetForRequirement = signal<boolean>(false);

  // Wählbare Ziele je Artikel-Typ. Bewusst Methode statt computed: der
  // Aktionstyp ist ein einfaches Feld und würde ein computed nicht neu auslösen.
  protected selectableConfigs(): ShippingConfig[] {
    const staging = this.formActionType === "STAGING";
    return this.configs().filter((c) =>
      staging
        ? !c.staging_shipped && COVERING_TYPES.includes(c.shipping_type)
        : !c.versand_shipped,
    );
  }

  // Vorauswahl spiegelt die Auto-Link-Regeln des Backends, damit der Default
  // dem bisherigen Verhalten entspricht: VERSAND an die neueste offene Config,
  // STAGING an die älteste deckende.
  private preselectTargetConfig() {
    const options = [...this.selectableConfigs()].sort((a, b) => a.id - b.id);
    if (options.length === 0) {
      this.formConfigId.set(this.formActionType === "STAGING" ? "none" : null);
      return;
    }
    const target = this.formActionType === "STAGING" ? options[0] : options[options.length - 1];
    this.formConfigId.set(target.id);
  }

  protected onTargetSelectChange(value: number | "none" | "new" | null) {
    if (value === "new") {
      const current = this.formConfigId();
      this.previousFormConfigId = current === "new" ? null : current;
      void this.openTargetDialogForRequirement();
      return;
    }
    this.formConfigId.set(value);
  }

  // "Neues Versandziel…": der Artikel-Dialog bleibt im Hintergrund stehen, der
  // Versandziel-Dialog legt die Config an, danach ist sie hier ausgewählt.
  private async openTargetDialogForRequirement() {
    this.editorLoading.set(true);
    try {
      this.editorContext.set(await this.configService.getContext(this.operationId));
    } catch {
      this.editorContext.set({ customer_has_return_address: false });
    } finally {
      this.editorLoading.set(false);
    }
    this.creatingTargetForRequirement.set(true);
  }

  protected async onRequirementTargetSaved(config: ShippingConfig) {
    this.creatingTargetForRequirement.set(false);
    // Voll laden, damit die neue Spalte auch die Matrix-Felder trägt.
    await this.reload();
    this.formConfigId.set(config.id);
  }

  protected onRequirementTargetCancelled() {
    this.creatingTargetForRequirement.set(false);
    this.formConfigId.set(this.previousFormConfigId);
  }

  // --- Modal oeffnen/schliessen ---
  protected openAdd(actionType: ActionType) {
    this.resetForm();
    this.formActionType = actionType;
    this.editingRequirement.set(null);
    this.preselectTargetConfig();
    this.showModal.set(true);
  }

  /**
   * Position, deren Automatik-Kennzeichen gerade umgeschaltet werden soll.
   * Der Dialog dazwischen ist Absicht: Die Folgen sind in beide Richtungen
   * nicht offensichtlich — automatisch heisst "wird nachts überschrieben",
   * manuell heisst "bleibt stehen, auch wenn der Bestand längst reicht".
   */
  protected automationToggle = signal<OperationRequirement | null>(null);
  protected automationSaving = signal(false);

  protected askAutomationToggle(req: OperationRequirement) {
    this.automationToggle.set(req);
  }

  protected async confirmAutomationToggle() {
    const req = this.automationToggle();
    if (!req) return;

    this.automationSaving.set(true);
    try {
      const updated = await this.reqService.updateRequirement(req.id, { auto_generated: !req.auto_generated });
      if (updated) await this.reload();
      this.automationToggle.set(null);
    } finally {
      this.automationSaving.set(false);
    }
  }

  protected openEdit(req: OperationRequirement) {
    this.resetForm();
    this.editingRequirement.set(req);
    // Die Matrix zeigt ausschließlich Versand-/Staging-Zeilen; Auftragsbedarf
    // (BEDARF) wird im eigenen Block darüber gepflegt und landet nie hier.
    this.formActionType = req.action_type === "STAGING" ? "STAGING" : "VERSAND";
    this.formQuantity = req.quantity;

    if (req.action_type === "STAGING" && req.article_template_id) {
      this.formStagingTemplateId = req.article_template_id;
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
    } else if (req.article_template_id) {
      this.selectedTemplate.set({ id: req.article_template_id, description: req.article_template_description || "" } as ArticleTemplate);
      this.formSearchText = req.article_template_description || "";
    } else {
      this.formSearchText = req.free_text || "";
    }

    this.showModal.set(true);
  }

  protected closeModal() {
    this.showModal.set(false);
    this.editingRequirement.set(null);
    this.formError.set(null);
    this.formConfigId.set(null);
    this.resetForm();
  }

  protected onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) this.closeModal();
  }

  // --- Formular-Logik ---
  protected onStagingTemplateSelect(templateId: number | null) {
    if (!templateId) {
      this.selectedTemplate.set(null);
      return;
    }
    const tpl = this.stagingTemplates().find((t) => t.id === templateId);
    if (tpl) this.selectedTemplate.set({ id: tpl.id, description: tpl.description } as ArticleTemplate);
  }

  protected onSearchInput() {
    this.selectedTemplate.set(null);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);

    if (this.formSearchText.trim().length < 2) {
      this.searchResults.set([]);
      return;
    }
    this.searchDebounce = setTimeout(() => this.performSearch(), 300);
  }

  private performSearch() {
    const query = this.formSearchText.toLowerCase();
    const results = this.allTemplates().filter(
      (t) => t.description?.toLowerCase().includes(query) || t.part_nr?.toLowerCase().includes(query),
    );
    this.searchResults.set(results.slice(0, 15));
  }

  protected selectTemplate(template: ArticleTemplate) {
    this.selectedTemplate.set(template);
    this.formSearchText = template.description;
    this.searchResults.set([]);
  }

  protected onSearchBlur() {
    this.searchResults.set([]);
  }

  protected formMinQuantity = computed<number>(() => {
    const editing = this.editingRequirement();
    if (!editing || editing.action_type !== "VERSAND") return 1;
    return Math.max(1, editing.already_shipped_quantity || 0);
  });

  protected canSave(): boolean {
    if (this.formQuantity < this.formMinQuantity()) return false;
    if (this.formActionType === "STAGING") return this.selectedTemplate() !== null;
    return this.selectedTemplate() !== null || this.formSearchText.trim().length > 0;
  }

  protected async saveRequirement() {
    const template = this.selectedTemplate();
    const data: Partial<OperationRequirement> = {
      action_type: this.formActionType,
      quantity: this.formQuantity,
    };

    if (template) {
      data.article_template_id = template.id;
      data.free_text = null as any;
    } else {
      data.free_text = this.formSearchText.trim();
      data.article_template_id = null as any;
    }

    const editing = this.editingRequirement();
    if (editing) {
      const updated = await this.reqService.updateRequirement(editing.id, data);
      if (updated) {
        this.closeModal();
        // Backend kann bei Versand-Mengen-Aenderung eine Auto-Config anlegen —
        // reload() laedt Requirements UND Configs neu.
        await this.reload();
      }
    } else {
      // Gewähltes Versandziel direkt mitschicken; "new" ist hier nie aktiv
      // (die Auswahl springt sofort in den Dialog), null = Backend wählt selbst.
      const chosen = this.formConfigId();
      const binding = chosen === "new" ? null : chosen;
      const result = await this.reqService.createRequirement(this.operationId, data, binding);
      if ("error" in result) {
        this.formError.set(result.error);
        return;
      }
      this.closeModal();
      await this.reload();
    }
  }

  // Versand-Artikel sind nur loeschbar, solange nichts versandt wurde; Staging-
  // Artikel jederzeit (Backend erzwingt die Detailregeln).
  protected canDelete(req: OperationRequirement): boolean {
    return req.action_type === "STAGING" || (req.already_shipped_quantity ?? 0) === 0;
  }

  protected async deleteRequirement(req: OperationRequirement) {
    const label = req.article_template_description || req.free_text || "Unbekannt";
    if (!confirm(`"${req.quantity}x ${label}" wirklich löschen?`)) return;
    const success = await this.reqService.deleteRequirement(req.id);
    if (success) await this.reload();
  }

  // Legt Versand-Requirements aus dem Legacy-Description-Parsing an. Nur erlaubt,
  // solange noch keine Versand-Requirements existieren (sonst Doppelungen).
  protected async parseFromDescription() {
    if (this.versandRows().length > 0) return;
    if (!confirm("Versand-Artikel aus der Auftragsbeschreibung erzeugen?")) return;

    this.parsingInProgress.set(true);
    try {
      const result = await this.reqService.parseFromDescription(this.operationId);
      if ("error" in result) {
        alert(result.error);
        return;
      }
      await this.reload();
    } finally {
      this.parsingInProgress.set(false);
    }
  }

  private resetForm() {
    this.formActionType = "VERSAND";
    this.formQuantity = 1;
    this.formSearchText = "";
    this.selectedTemplate.set(null);
    this.formStagingTemplateId = null;
    this.searchResults.set([]);
  }
}
